/**
 * ClientQuickBar
 * --------------
 * A fixed quick-action bar pinned to the bottom of the client app, mirroring the
 * clinic app's dashboard quick bar: flat side actions with a raised primary
 * action in the middle. Presentation only — no auth/session logic.
 *
 * It is hidden on auth / full-screen routes and when signed out, and reserves
 * body padding so page content never hides behind it. RTL-aware via document flow.
 */
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { Box, Container, ButtonBase, Typography, GlobalStyles, Badge } from '@mui/material';
import { alpha } from '@mui/material/styles';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import MedicalInformationOutlinedIcon from '@mui/icons-material/MedicalInformationOutlined';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import { useAuth } from '@/hooks/useAuth';
import { selectCurrentClinic } from '@/store/clinicSlice';
import { notificationsApi, consultationsApi } from '@/services/api';

const TONE = '#11223E';
const HIDE_PREFIXES = ['/login', '/otp', '/welcome', '/signup', '/clinic-entry', '/report', '/face-capture', '/checkout'];

/** A flat side action: icon above a short label. */
function BarItem({ icon, label, onClick, badge = 0 }) {
  return (
    <ButtonBase onClick={onClick} aria-label={label} focusRipple sx={{
      flex: 1, maxWidth: 84, minWidth: 0, display: 'flex', flexDirection: 'column',
      alignItems: 'center', gap: 0.25, py: 0.75, borderRadius: 2, color: 'text.secondary',
      transition: 'color .15s ease',
      '&:hover': { color: TONE }, '&:active': { color: TONE },
      '&.Mui-focusVisible': { outline: `2px solid ${TONE}`, outlineOffset: 2 },
      '& svg': { fontSize: 23 },
    }}>
      <Badge color="error" badgeContent={badge} max={99}>{icon}</Badge>
      <Typography sx={{
        fontSize: 10.5, fontWeight: 700, lineHeight: 1.1,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%',
      }}>
        {label}
      </Typography>
    </ButtonBase>
  );
}

/**
 * A raised primary action. There are two of them, side by side in the middle:
 * starting a consultation, and the client's own card.
 *
 * `from`/`to` give each its own gradient so the pair reads as two distinct
 * destinations rather than one wide button — navy for the care itself, brass
 * for the card, matching how both are coloured everywhere else in the app.
 */
function CenterItem({ icon, label, onClick, from = TONE, to = '#2C4A78', badge = 0 }) {
  return (
    <ButtonBase onClick={onClick} aria-label={label} focusRipple sx={{
      flex: 1, maxWidth: 84, minWidth: 0, display: 'flex', flexDirection: 'column',
      alignItems: 'center', gap: 0.5, borderRadius: 3,
      '&.Mui-focusVisible .qb-fab': { outline: `2px solid ${from}`, outlineOffset: 2 },
    }}>
      <Badge color="error" badgeContent={badge} max={99} overlap="circular">
        <Box className="qb-fab" sx={{
          mt: -3, width: 52, height: 52, borderRadius: '17px', display: 'grid', placeItems: 'center',
          color: '#fff', background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`,
          boxShadow: `0 10px 22px -8px ${alpha(from, 0.75)}`, border: '3px solid', borderColor: 'background.paper',
          '& svg': { fontSize: 26 },
        }}>
          {icon}
        </Box>
      </Badge>
      <Typography sx={{
        fontSize: 10.5, fontWeight: 800, lineHeight: 1, color: from,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%',
      }}>
        {label}
      </Typography>
    </ButtonBase>
  );
}

export default function ClientQuickBar() {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentClinic = useSelector(selectCurrentClinic);
  const canSelfCapture = currentClinic?.capturePolicy === 'client_capture';

  const { data: unreadResp } = useQuery({
    queryKey: ['unread-summary-quickbar'],
    queryFn: () => notificationsApi.getUnreadSummary(),
    enabled: isAuthenticated,
    staleTime: 30000,
  });
  const unreadTotal = unreadResp?.data?.total ?? 0;

  const { data: consultAlerts } = useQuery({
    queryKey: ['consultation-alerts'],
    queryFn: () => consultationsApi.listConsultations().then((list) =>
      list.reduce((n, c) => n + (c.unreadCount || 0), 0)),
    enabled: isAuthenticated,
    staleTime: 60000,
  });

  // On the public entry (`/start`) the bar is SHOWN but inert and greyed: it
  // previews what the app becomes once the visitor verifies, and the moment
  // they do it comes alive in place — no reload, no navigation.
  const onPublicEntry = pathname.startsWith('/start') && !pathname.startsWith('/start-consultation');
  const inactive = !isAuthenticated;
  const hidden =
    (!isAuthenticated && !onPublicEntry) || HIDE_PREFIXES.some((p) => pathname.startsWith(p));
  if (hidden) return null;

  const go = (r) => { if (!inactive) navigate(r); };

  return (
    <>
      <GlobalStyles styles={{ body: { paddingBottom: 'calc(74px + env(safe-area-inset-bottom))' } }} />
      <Box component="nav" sx={{
        position: 'fixed', insetInline: 0, bottom: 0, zIndex: (th) => th.zIndex.appBar,
        // inert state: desaturated and non-interactive, but still legible
        filter: inactive ? 'grayscale(1)' : 'none',
        opacity: inactive ? 0.55 : 1,
        pointerEvents: inactive ? 'none' : 'auto',
        transition: 'opacity .35s ease, filter .35s ease',
        bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider',
        boxShadow: '0 -6px 20px -12px rgba(17,34,62,0.35)', pb: 'env(safe-area-inset-bottom)',
      }}>
        <Container maxWidth="sm" sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', gap: 0.25, py: 0.75, minHeight: 58 }}>
          <BarItem icon={<SpaceDashboardIcon />} label={t('quickbar.home')} onClick={() => go('/dashboard')} />
          <BarItem icon={<MedicalInformationOutlinedIcon />} label={t('quickbar.consultations')} onClick={() => go('/consultations')} badge={consultAlerts || 0} />
          {/* Two raised actions, side by side: the way into a consultation,
              and the client's own card. Everything else on the bar is a flat
              side action, as before. */}
          <CenterItem
            icon={<CardMembershipIcon />}
            label={t('quickbar.candleCard')}
            from="#8C6D1F"
            to="#B7994A"
            onClick={() => go('/candle-card')}
          />
          <CenterItem
            icon={<MedicalInformationIcon />}
            label={t('quickbar.startConsultation')}
            onClick={() => go('/start-consultation')}
          />
          {/* Clients whose clinic allows self-capture keep a direct capture
              shortcut; everyone else keeps the catalogue. History stays
              reachable from the dashboard either way. */}
          {canSelfCapture
            ? <BarItem icon={<FaceRetouchingNaturalIcon />} label={t('quickbar.capture')} onClick={() => go('/face-capture')} />
            : <BarItem icon={<MedicalServicesIcon />} label={t('quickbar.services')} onClick={() => go('/services')} />}
          <BarItem icon={<ContactSupportOutlinedIcon />} label={t('quickbar.support')} onClick={() => go('/support')} badge={unreadTotal} />
        </Container>
      </Box>
    </>
  );
}
